import React, { useEffect, useRef, useState } from "react";
import { Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { CustomModal } from "../utils/custom-elements/Index";
import { useSideNavMenu } from "../side-nav-menu/Index";
import { getDefaultWebHomePageShortcuts } from "../page-layout/Index";
import { getOrgDetails, getProfile, getSelectedOrgId, getUserOrgDetails } from "../../thunks/user-management";
import { trnTypes, userOrgRoles } from "../../constants";
import { addAppTab, useMouseTrap } from "../../utils";

const MAX_RECENT = 8;
const MAX_RESULTS = 50;

// Extra search words per menu key: Tally names, abbreviations and common alternate names
const keywordsByMenuKey = {
  MASTER__ACCOUNT: "ledger master party customer supplier debtor creditor",
  MASTER__ACCOUNT_GROUP: "group master",
  MASTER__PRODUCT__PRODUCT: "item stock item",
  MASTER__PRODUCT__PRODUCT_SCREEN: "design item quality",
  MASTER__PRODUCT__HSN_MASTER: "hsn sac gst",
  MASTER__OTHER__FIRM: "company",
  MASTER__OTHER__BANK: "drawee bank",
  MASTER__OTHER__SALESPERSON: "agent",
  TRNS__SAL__SALES: "invoice bill sale",
  TRNS__SAL__SALES_RETURN: "rg return goods",
  TRNS__SAL__EXCESS_SALES_RETURN: "excess return goods",
  TRNS__PUR__PURCHASE: "invoice bill",
  TRNS__PUR__PURCHASE_RETURN: "return goods",
  TRNS__PAYMENTS__RECEIPT: "collection received",
  TRNS__PAYMENTS__PAYMENT: "paid",
  TRNS__PAYMENTS__MULTI_RECEIPT: "bulk receipt",
  TRNS__PAYMENTS__MULTI_PAYMENT: "bulk payment",
  TRNS__JOURNAL__JOURNAL: "jv",
  TRNS__JOURNAL__CONTRA: "contra",
  TRNS__JOURNAL__MULTI_JOURNAL: "bulk jv",
  TRNS__MILL__ISSUE: "grey issue",
  TRNS__MILL__RECEIVE: "process",
  REPORTS__LEDGER__LEDGER: "account statement party ledger",
  REPORTS__ACCOUNT_GROUP__BALANCE: "group summary",
  REPORTS__OUTSTANDING__SAL: "receivable debtors bills receivable",
  REPORTS__OUTSTANDING__PUR: "payable creditors bills payable",
  REPORTS__OUTSTANDING_90DAYS__SAL: "ageing receivable",
  REPORTS__OUTSTANDING_90DAYS__PUR: "ageing payable",
  REPORTS__TRNS_INTEREST: "late payment interest",
  REPORTS__BANK__RECON: "brs bank reconciliation",
  REPORTS__BANK__AUTO_BRS: "brs bank reconciliation statement",
  REPORTS__TRN__REGISTER: "day book daybook",
  REPORTS__GSTR__1: "gst return",
  REPORTS__GSTR__2A: "gst return",
  REPORTS__GSTR__2B: "gst return reconciliation",
  REPORTS__GSTR__3B: "gst return",
  REPORTS__GSTR__9: "gst annual return",
  REPORTS__FINAL__TRIAL_BALANCE: "tb",
  REPORTS__FINAL__PNL: "profit loss pnl p&l",
  REPORTS__FINAL__BALANCE_SHEET: "bs",
  STK_REPORTS__SUMMARY: "inventory",
  STK_REPORTS__BALANCE: "inventory",
  SETTINGS__USERS: "roles permissions",
  SETTINGS__FCYEARS: "financial year fy",
  SETTINGS__YEAREND: "year end closing carry forward",
  SETTINGS__DATA_LOCKING: "lock",
};

// Screens that are only reachable through a global hotkey (pages/app/Index.jsx), not the menu
const hotkeyOnlyScreens = [
  { id: "HOTKEY__ORDERS", title: "Orders Report", path: "/reports/orders", breadcrumb: ["Reports"] },
  {
    id: "HOTKEY__SALES_OUTSTANDING_SUMMARY",
    title: "Sales Outstanding Summary",
    path: "/reports/outstanding/zoom-reports/summary/sales",
    breadcrumb: ["Reports", "Outstanding"],
    keywords: "receivable debtors",
  },
  {
    id: "HOTKEY__PURCHASE_OUTSTANDING_SUMMARY",
    title: "Purchase Outstanding Summary",
    path: "/reports/outstanding/zoom-reports/summary/purchase",
    breadcrumb: ["Reports", "Outstanding"],
    keywords: "payable creditors",
  },
  { id: "HOTKEY__ITEM_RATE_LIST", title: "Item Rate List", path: "/reports/item-rates-list", breadcrumb: ["Reports"], keywords: "price list" },
];

// Keep in sync with the global hotkeys in pages/app/Index.jsx
const hotkeyByPath = {
  "/reports/ledger/ledger": "F5",
  "/reports/orders": "F6",
  "/reports/outstanding/zoom-reports/summary/sales": "F7",
  "/reports/outstanding/zoom-reports/summary/purchase": "F8",
  "/reports/item-rates-list": "F9",
  "/reports/interest": "F11",
  "/reports/ledger/account-group-balance": "Ctrl+F5",
  "/reports/final-reports/TRIAL_BALANCE": "Ctrl+F6",
  "/reports/final-reports/TRADING_PNL": "Ctrl+F7",
  "/reports/final-reports/BALANCE_SHEET": "Ctrl+F8",
  "/reports/bank/slip": "Ctrl+B",
  "/reports/bank/recon": "Alt+B",
  "/reports/ledger/bank-statement": "Ctrl+Shift+B",
};
[
  trnTypes.SALES,
  trnTypes.PURCHASE,
  trnTypes.RECEIPT_ENTRY,
  trnTypes.PAYMENT_ENTRY,
  trnTypes.MILL_ISSUE,
  trnTypes.MILL_RECEIVE,
  trnTypes.JOB_WORK_ISSUE,
  trnTypes.JOB_WORK_RECEIVE,
].forEach((trnType, index) => {
  hotkeyByPath[`/transaction/${trnType}`] = `Ctrl+${index + 1}`;
  hotkeyByPath[`/transaction/${trnType}/new`] = `Alt+${index + 1}`;
});

const normalize = (text) => (text || "").toLowerCase().replace(/[^a-z0-9&]+/g, " ").trim();

const getEntryId = (key, state) => (state ? `${key}|${JSON.stringify(state)}` : key);

// Walks the side menu tree (already filtered by permissions, modules and industry) into a flat list of screens
const buildEntries = (menuTree, menuItems, canCreateTrn) => {
  const entries = [];
  const seenIds = new Set();
  const walk = (nodes, parentLabels) => {
    (nodes || []).forEach((node) => {
      if (!node) return;
      const labels = [...parentLabels, node.label];
      if (node.children) {
        walk(node.children, labels);
        return;
      }
      const menuItem = menuItems[node.key];
      if (!menuItem?.path) return;
      const state = node.state ?? menuItem.state;
      const id = getEntryId(node.key, state);
      if (seenIds.has(id)) return;
      seenIds.add(id);
      const trnType = (menuItem.path.match(/^\/transaction\/([A-Z_]+)$/) || [])[1];
      const keywords = [keywordsByMenuKey[node.key], trnType].filter(Boolean).join(" ");
      entries.push({
        id,
        title: menuItem.title,
        label: node.label,
        path: menuItem.path,
        state,
        breadcrumb: labels,
        keywords,
        hotkey: state ? undefined : hotkeyByPath[menuItem.path],
      });
      if (trnType && canCreateTrn(trnType)) {
        const newPath = `${menuItem.path}/new`;
        entries.push({
          id: `${id}|NEW`,
          title: `New ${menuItem.title}`,
          label: `New ${node.label}`,
          tabTitle: menuItem.title,
          path: newPath,
          breadcrumb: [...labels, "New"],
          keywords: `create add entry ${keywords}`,
          hotkey: hotkeyByPath[newPath],
        });
      }
    });
  };
  walk(menuTree, []);
  hotkeyOnlyScreens.forEach((obj) => entries.push({ ...obj, hotkey: hotkeyByPath[obj.path] }));
  return entries.map((entry) => ({
    ...entry,
    searchTitle: normalize(entry.title),
    searchLabel: normalize(entry.label),
    searchBreadcrumb: normalize(entry.breadcrumb.join(" ")),
    searchKeywords: normalize(entry.keywords),
  }));
};

const getWords = (text) => text.split(" ").filter(Boolean);

// Scores how well a normalized query matches one normalized field. 0 = no match.
const scoreField = (field, query) => {
  if (!field) return 0;
  if (field === query) return 100;
  if (field.startsWith(query)) return 90;
  const words = getWords(field);
  if (words.some((word) => word.startsWith(query))) return 80;
  // Multi-word phrase starting at a word ("sales reg" in "net sales register") beats matching the words separately
  if (` ${field}`.includes(` ${query}`)) return 78;
  if (!query.includes(" ") && query.length >= 2 && words.map((word) => word[0]).join("").startsWith(query)) return 75;
  const queryWords = getWords(query);
  if (queryWords.length > 1 && queryWords.every((qWord) => words.some((word) => word.startsWith(qWord)))) return 70;
  if (field.includes(query)) return 60;
  return 0;
};

// Subsequence match ("slsrg" → "sales return goods"), used only as a last resort
const isSubsequence = (text, query) => {
  const compactQuery = query.replace(/ /g, "");
  let index = 0;
  for (const char of text) {
    if (char === compactQuery[index]) index++;
    if (index === compactQuery.length) return true;
  }
  return false;
};

const scoreEntry = (entry, query) => {
  // The menu label (e.g. "Ledger" for Account Ledger) counts as much as the title
  const titleScore = Math.max(scoreField(entry.searchTitle, query), scoreField(entry.searchLabel, query));
  const otherScore = Math.max(scoreField(entry.searchBreadcrumb, query), scoreField(entry.searchKeywords, query)) * 0.8;
  let score = Math.max(titleScore, otherScore);
  if (!score) {
    // Every query word must match the start of some word across title, breadcrumb and keywords
    const allWords = getWords(`${entry.searchTitle} ${entry.searchBreadcrumb} ${entry.searchKeywords}`);
    const queryWords = getWords(query);
    if (queryWords.every((qWord) => allWords.some((word) => word.startsWith(qWord)))) score = 50;
  }
  if (!score && query.length >= 3 && isSubsequence(entry.searchTitle, query)) score = 20;
  return score;
};

// Returns [start, end) ranges of the title to highlight for the query
const getHighlightRanges = (title, query) => {
  const lowerTitle = title.toLowerCase();
  const queryWords = getWords(normalize(query));
  if (queryWords.length === 0) return [];
  const fullIndex = lowerTitle.indexOf(queryWords.join(" "));
  if (fullIndex >= 0) return [[fullIndex, fullIndex + queryWords.join(" ").length]];
  const ranges = [];
  queryWords.forEach((qWord) => {
    const match = new RegExp(`(^|[^a-z0-9])(${qWord.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`).exec(lowerTitle);
    if (match) {
      const start = match.index + match[1].length;
      ranges.push([start, start + qWord.length]);
    }
  });
  return ranges.sort((a, b) => a[0] - b[0]);
};

const HighlightedText = ({ text, query }) => {
  const ranges = query ? getHighlightRanges(text, query) : [];
  if (ranges.length === 0) return <>{text}</>;
  const parts = [];
  let cursor = 0;
  ranges.forEach(([start, end], index) => {
    if (start < cursor) return;
    if (start > cursor) parts.push(<span key={`t${index}`}>{text.slice(cursor, start)}</span>);
    parts.push(<b key={`b${index}`} style={{ color: "#0958d9" }}>{text.slice(start, end)}</b>);
    cursor = end;
  });
  if (cursor < text.length) parts.push(<span key="rest">{text.slice(cursor)}</span>);
  return <>{parts}</>;
};

const getRecentStorageKey = (orgId) => `goToPaletteRecent_${orgId}`;

const getRecentIds = (orgId) => {
  try {
    const ids = JSON.parse(localStorage.getItem(getRecentStorageKey(orgId)) || "[]");
    return Array.isArray(ids) ? ids : [];
  } catch (error) {
    return [];
  }
};

const saveRecentId = (orgId, id) => {
  const ids = [id, ...getRecentIds(orgId).filter((obj) => obj !== id)].slice(0, MAX_RECENT);
  localStorage.setItem(getRecentStorageKey(orgId), JSON.stringify(ids));
};

const GoToPalette = ({ handleCancel, inputRef }) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalElement, setModalElement] = useState();
  const navigatedRef = useRef(false);
  const rowRefs = useRef([]);
  const flatItemsRef = useRef([]);
  const activeIndexRef = useRef(0);
  const { menuItems, menuTree } = useSideNavMenu();
  const selectedOrgId = useSelector((state) => getSelectedOrgId(state));
  const userOrgDetails = useSelector((state) => getUserOrgDetails(state));
  const industry = useSelector((state) => getOrgDetails(state)).industry;
  const { settings } = useSelector((state) => getProfile(state));
  const isOwner = userOrgDetails.role === userOrgRoles.OWNER;
  const trnPermissions = userOrgDetails.permissions?.transactions;

  // ~200 entries: cheap enough to rebuild on every render, which also keeps them in sync with the menu
  const entries = buildEntries(menuTree, menuItems, (trnType) => isOwner || trnPermissions?.[trnType]?.create);

  const getSections = () => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) {
      const entriesById = Object.fromEntries(entries.map((obj) => [obj.id, obj]));
      const recent = getRecentIds(selectedOrgId).map((id) => entriesById[id]).filter(Boolean);
      const recentPaths = new Set(recent.map((obj) => obj.path));
      const homePageShortcuts = settings?.webHomePageShortcuts ?? getDefaultWebHomePageShortcuts(industry);
      const shortcuts = (homePageShortcuts || [])
        .filter((obj) => obj.id && !recentPaths.has(obj.id))
        .map((obj) => {
          const entry = entries.find((entry) => entry.path === obj.id && !entry.state);
          return entry ? { ...entry, title: obj.name, tabTitle: obj.name } : { id: `SHORTCUT|${obj.id}`, title: obj.name, path: obj.id, breadcrumb: [] };
        });
      return [
        { title: "Recent", items: recent },
        { title: "Shortcuts", items: shortcuts },
      ].filter((obj) => obj.items.length > 0);
    }
    const recentIds = getRecentIds(selectedOrgId);
    const results = entries
      .map((entry) => {
        const score = scoreEntry(entry, normalizedQuery);
        return { entry, score: score ? score + (recentIds.includes(entry.id) ? 5 : 0) : 0 };
      })
      .filter((obj) => obj.score > 0)
      .sort((a, b) => b.score - a.score || a.entry.title.length - b.entry.title.length)
      .slice(0, MAX_RESULTS)
      .map((obj) => obj.entry);
    return [{ title: null, items: results }];
  };

  const sections = getSections();
  const flatItems = sections.flatMap((obj) => obj.items);
  // Key handlers are bound once and read the latest list and row from these refs
  flatItemsRef.current = flatItems;
  activeIndexRef.current = activeIndex;

  // react-modal focuses its content box after opening, so focus the input after that
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus());
  }, []);

  useEffect(() => {
    rowRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const openEntry = (entry) => {
    if (!entry) return;
    navigatedRef.current = true;
    if (!entry.id.startsWith("SHORTCUT|")) saveRecentId(selectedOrgId, entry.id);
    handleCancel();
    setTimeout(() => addAppTab(entry.path, entry.tabTitle || entry.title, entry.state));
  };
  const openEntryRef = useRef();
  openEntryRef.current = openEntry;

  // Bound once per modal element (each rebind leaves Mousetrap's DOM listeners behind)
  useMouseTrap({ current: modalElement }, {
    up: (e) => {
      e.preventDefault();
      const count = flatItemsRef.current.length;
      setActiveIndex((index) => (count ? (index - 1 + count) % count : 0));
    },
    down: (e) => {
      e.preventDefault();
      const count = flatItemsRef.current.length;
      setActiveIndex((index) => (count ? (index + 1) % count : 0));
    },
    enter: (e) => {
      e.preventDefault();
      openEntryRef.current(flatItemsRef.current[activeIndexRef.current]);
    },
  }, []);

  let rowIndex = -1;
  return (
    <CustomModal
      onCancel={handleCancel}
      width="640px"
      contentRef={setModalElement}
      contentStyle={{ padding: 0, top: "8%" }}
      returnFocusFunc={(triggerElement) => {
        // When a screen was opened, the new tab takes focus; don't pull it back to the old one
        if (!navigatedRef.current) setTimeout(() => triggerElement.current?.focus?.());
      }}
    >
      <div style={{ padding: 12, borderBottom: "1px solid #eee" }}>
        <Input
          ref={inputRef}
          size="large"
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          placeholder="Go to screen… (e.g. ledger, sales register, new receipt)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          allowClear
        />
      </div>
      <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "4px 0" }}>
        {flatItems.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "#999" }}>
            {query ? "No matching screen" : "Type to search screens"}
          </div>
        )}
        {sections.map((section) => (
          <div key={section.title || "results"}>
            {section.title && (
              <div style={{ padding: "8px 16px 4px", fontSize: 12, fontWeight: "bold", color: "#888", textTransform: "uppercase" }}>
                {section.title}
              </div>
            )}
            {section.items.map((entry) => {
              rowIndex++;
              const index = rowIndex;
              const isActive = index === activeIndex;
              return (
                <div
                  key={entry.id}
                  ref={(el) => (rowRefs.current[index] = el)}
                  onClick={() => openEntry(entry)}
                  onMouseMove={() => !isActive && setActiveIndex(index)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "6px 16px",
                    cursor: "pointer",
                    background: isActive ? "#e6f4ff" : undefined,
                    borderLeft: `3px solid ${isActive ? "#1677ff" : "transparent"}`,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14 }}>
                      <HighlightedText text={entry.title} query={query} />
                    </div>
                    {entry.breadcrumb.length > 0 && (
                      <div style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {entry.breadcrumb.join(" › ")}
                      </div>
                    )}
                  </div>
                  {entry.hotkey && (
                    <span
                      style={{
                        marginLeft: 12, padding: "0 6px", fontSize: 12, color: "#555", border: "1px solid #ccc", borderRadius: 4, background: "#fafafa"
                      }}
                    >
                      {entry.hotkey}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div style={{ padding: "6px 16px", borderTop: "1px solid #eee", fontSize: 12, color: "#888" }}>
        ↑↓ to move · Enter to open in new tab · Esc to close
      </div>
    </CustomModal>
  );
};

// Search bar for the topbar. Owns the Alt+G hotkey and the palette.
export const GoToSearchBar = () => {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);
  visibleRef.current = visible;
  const inputRef = useRef();
  // Bound once: each rebind on document.body would leave Mousetrap's DOM listeners behind for the whole session
  useMouseTrap({ current: document.body }, {
    "alt+g": (e) => {
      e.preventDefault();
      if (visibleRef.current) {
        inputRef.current?.focus();
        inputRef.current?.select?.();
      } else {
        setVisible(true);
      }
    },
  }, []);
  return (
    <>
      <div
        onClick={() => setVisible(true)}
        style={{
          display: "flex",
          alignItems: "center",
          width: 320,
          padding: "3px 10px",
          background: "white",
          border: "1px solid #ccc",
          borderRadius: 6,
          color: "#999",
          cursor: "pointer",
        }}
      >
        <SearchOutlined style={{ marginRight: 8 }} />
        <span style={{ flex: 1, textAlign: "left" }}>Search screens…</span>
        <span style={{ padding: "0 6px", fontSize: 12, border: "1px solid #ddd", borderRadius: 4, background: "#fafafa" }}>Alt+G</span>
      </div>
      {visible && <GoToPalette handleCancel={() => setVisible(false)} inputRef={inputRef} />}
    </>
  );
};
